import { CDN_UPLOAD_MAX_BYTES } from '../../lib/subscriberCustomFields';
import { supabase } from '../../lib/supabase';
import type { CdnListResult, CdnObject, CdnProfile } from './types';

const CDN_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cdn-assets`;

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw new Error(error.message);
  const token = data.session?.access_token;
  if (!token) throw new Error('Not signed in — refresh Engine and try again.');
  return token;
}

async function cdnFunctionFetch<T>(
  body: Record<string, unknown>,
  opts?: { retries?: number },
): Promise<T> {
  const retries = opts?.retries ?? 0;
  const token = await getAccessToken();
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 400 * attempt));
    }

    let res: Response;
    try {
      res = await fetch(CDN_FUNCTION_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (e) {
      lastError = new Error(e instanceof Error ? e.message : 'CDN request failed');
      continue;
    }

    let parsed: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        parsed = JSON.parse(text) as unknown;
      } catch {
        parsed = { error: text };
      }
    }

    if (res.ok) return parsed as T;

    const detail =
      parsed && typeof parsed === 'object' && 'error' in parsed &&
        typeof (parsed as { error: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : text || res.statusText;

    if (res.status === 503 && attempt < retries) {
      lastError = new Error(
        'CDN edge function unavailable (503). Retrying…',
      );
      continue;
    }

    if (res.status === 503) {
      throw new Error(
        'CDN edge function unavailable (503). Hard-refresh Engine (Cmd+Shift+R) and retry.',
      );
    }

    throw new Error(detail || `CDN request failed (${res.status})`);
  }

  throw lastError ?? new Error('CDN request failed');
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  return cdnFunctionFetch<T>(body, { retries: 1 });
}

/** prepareUpload returns this when the key exists and overwrite is not set. */
export function cdnObjectAlreadyExists(error: unknown): boolean {
  return error instanceof Error && /already exists/i.test(error.message);
}

export async function listCdnProfiles(): Promise<CdnProfile[]> {
  const data = await invoke<{ profiles?: CdnProfile[] }>({ action: 'listProfiles' });
  return data.profiles ?? [];
}

export async function listCdnObjects(opts: {
  profileId: string;
  prefix?: string;
  continuationToken?: string;
}): Promise<CdnListResult> {
  return invoke<CdnListResult>({
    action: 'list',
    profileId: opts.profileId,
    prefix: opts.prefix ?? '',
    continuationToken: opts.continuationToken,
  });
}

/**
 * Upload via cdn-assets edge function: prepareUpload (small JSON) then browser
 * PUT to R2 presigned URL. File bytes never pass through Supabase gateway
 * (multipart/base64 uploads 503 before the edge function runs).
 */
export async function uploadCdnObject(opts: {
  profileId: string;
  key: string;
  file: File;
  overwrite?: boolean;
}): Promise<CdnObject> {
  const file = await compressImageForUpload(opts.file);
  const key = normalizeUploadKeyForFile(opts.key, file);

  return uploadViaPresignedEdge({
    profileId: opts.profileId,
    key,
    file,
    overwrite: opts.overwrite,
  });
}

async function compressImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.size <= CDN_UPLOAD_MAX_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const maxDim = 2048;
    let { width, height } = bitmap;
    if (width > maxDim || height > maxDim) {
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    let quality = 0.88;
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 6; attempt++) {
      blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      });
      if (blob && blob.size <= CDN_UPLOAD_MAX_BYTES) break;
      quality -= 0.08;
    }

    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'upload';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function normalizeUploadKeyForFile(key: string, file: File): string {
  const ext =
    file.type === 'image/jpeg'
      ? 'jpg'
      : file.name.includes('.')
        ? file.name.split('.').pop()?.toLowerCase() || 'jpg'
        : 'jpg';
  const base = key.replace(/\.[^./]+$/, '');
  return `${base}.${ext === 'jpeg' ? 'jpg' : ext}`;
}

async function uploadViaPresignedEdge(opts: {
  profileId: string;
  key: string;
  file: File;
  overwrite?: boolean;
}): Promise<CdnObject> {
  const prep = await invoke<{
    key: string;
    putUrl: string;
    publicUrl: string;
    contentType: string;
  }>({
    action: 'prepareUpload',
    profileId: opts.profileId,
    key: opts.key,
    contentType: opts.file.type || undefined,
    overwrite: opts.overwrite === true ? true : undefined,
    mobileLink: false,
  });

  if (!prep.putUrl || !prep.publicUrl) {
    throw new Error('CDN prepareUpload did not return a put URL');
  }

  if (!/^https:\/\/[^/]+\.r2\.cloudflarestorage\.com\//i.test(prep.putUrl)) {
    throw new Error(
      'CDN prepareUpload returned an invalid upload URL. Redeploy cdn-assets edge function.',
    );
  }

  const contentType =
    prep.contentType?.trim() ||
    opts.file.type?.trim() ||
    'application/octet-stream';

  let putRes: Response;
  try {
    putRes = await fetch(prep.putUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: opts.file,
    });
  } catch {
    throw new Error(
      'CDN presigned upload failed (network). Check R2 bucket CORS allows PUT from this origin (engine.lowcodecto.com, engine.stephansmith.me, or localhost:5173–5175).',
    );
  }

  if (!putRes.ok) {
    const detail = await putRes.text().catch(() => '');
    throw new Error(
      `R2 upload failed (${putRes.status})${detail ? `: ${detail.slice(0, 200)}` : ''}`,
    );
  }

  return {
    key: prep.key ?? opts.key,
    size: opts.file.size,
    lastModified: new Date().toISOString(),
    publicUrl: prep.publicUrl,
  };
}

export async function deleteCdnObject(opts: {
  profileId: string;
  key: string;
}): Promise<void> {
  await invoke<{ ok: true }>({
    action: 'delete',
    profileId: opts.profileId,
    key: opts.key,
  });
}

export async function renameCdnObject(opts: {
  profileId: string;
  fromKey: string;
  toKey: string;
}): Promise<CdnObject> {
  const data = await invoke<{ object: CdnObject }>({
    action: 'rename',
    profileId: opts.profileId,
    fromKey: opts.fromKey,
    toKey: opts.toKey,
  });
  return data.object;
}

export function objectDir(key: string): string {
  const idx = key.lastIndexOf('/');
  return idx === -1 ? '' : key.slice(0, idx + 1);
}

export function objectFilename(key: string): string {
  const idx = key.lastIndexOf('/');
  return idx === -1 ? key : key.slice(idx + 1);
}

export function joinObjectKey(dir: string, filename: string): string {
  const name = filename.trim().replace(/^\//, '');
  if (!name || name.includes('/') || name.includes('..')) return '';
  const prefix = dir.replace(/^\//, '').replace(/\.\./g, '');
  return prefix ? `${prefix.replace(/\/+$/, '')}/${name}` : name;
}

export function isImageKey(key: string): boolean {
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(key);
}

export function isQrMarkKey(key: string): boolean {
  return /\.(png|jpe?g|webp)$/i.test(key);
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function prefixLabel(prefix: string): string {
  if (!prefix) return 'Root';
  return prefix.replace(/\/$/, '');
}

export function folderName(prefix: string): string {
  const trimmed = prefix.replace(/\/$/, '');
  const parts = trimmed.split('/');
  return parts[parts.length - 1] || trimmed;
}

/** Folder path for browse/list. Empty = root. Always trailing slash when set. */
export function normalizeBrowsePrefix(prefix: string | null | undefined): string {
  if (!prefix) return '';
  const cleaned = prefix
    .trim()
    .replace(/^\//, '')
    .replace(/\.\./g, '')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '');
  return cleaned ? `${cleaned}/` : '';
}

/** Search string for `/cdn` so refresh keeps folder + profile. Slashes stay readable. */
export function cdnFolderSearch(opts: { prefix: string; profileId?: string }): string {
  const parts: string[] = [];
  const folder = normalizeBrowsePrefix(opts.prefix).replace(/\/$/, '');
  if (folder) parts.push(`prefix=${encodeURIComponent(folder).replace(/%2F/gi, '/')}`);
  if (opts.profileId) parts.push(`profile=${encodeURIComponent(opts.profileId)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}
